import React, {ChangeEvent, useMemo, useState} from 'react';
import InfoIcon from "../components/info-icon";
import {getRandomDashboardHeroImage} from "../helpers/dashboardHeroImages";
import * as localforage from "localforage";
import {loadDataFile, parseData} from "ebird-mydata-reader/dist/cjs/ebird-mydata-reader";
import {
    clearDerivedBirdDataCaches,
    LOCATION_ECO_DATA_CACHE_KEY,
    LOCATION_ECO_DATA_CACHE_VERSION
} from "../helpers/birdDataCache";
import {
    enrichEcoregionLocations,
    getUniqueEcoregionEnrichmentLocations
} from "../api/ecoregions";
import './data-onboarding.scss';

export type UploadDataProps = {
    onSetData: React.Dispatch<any>
    onSetYears: React.Dispatch<any>
    onResetDataRequest: (e: React.MouseEvent) => void,
}

export default function UploadData({onSetData, onResetDataRequest, onSetYears}: UploadDataProps) {
    const [processingMessage, setProcessingMessage] = useState('');
    const onboardingHeroImage = useMemo(() => getRandomDashboardHeroImage(), []);
    const isProcessing = Boolean(processingMessage);

    const onFileChange = (e: ChangeEvent) => {
        const target = e.target as HTMLInputElement;
        if (target.files?.length) {
            const dataFile = target.files[0];
            const fr = new FileReader();
            setProcessingMessage('Reading your ZIP file and getting your sightings ready.');

            fr.addEventListener('load', async (e) => {
                try {
                    const csvData = await loadDataFile(e.target?.result as ArrayBuffer);
                    const jsonData = parseData(csvData);

                    await processData(jsonData);
                } catch (error) {
                    console.error(error);
                    setProcessingMessage('');
                }
            });

            fr.addEventListener('error', () => {
                setProcessingMessage('');
            });

            fr.readAsArrayBuffer(dataFile);
        }
    };

    const processData = async (jsonData: any) => {
        await clearDerivedBirdDataCaches();
        await enrichAndCacheEcoregionData(jsonData);
        await localforage.setItem('storedEbirdData', {
            date: new Date(),
            data: jsonData
        });
        onSetData(jsonData);

        let years = [];
        for (let row of jsonData) {
            if (!row.date) continue;

            const year = parseInt(row.date.split('-')[0]);

            if (years.indexOf(year) < 0) {
                years.push(year);
            }
        }

        years = years.sort().reverse();
        onSetYears(years);
    }

    const enrichAndCacheEcoregionData = async (jsonData: any[]) => {
        const locations = getUniqueEcoregionEnrichmentLocations(jsonData);
        if (!locations.length) return;

        setProcessingMessage(`Adding biome and ecoregion context for ${locations.length.toLocaleString()} locations.`);

        try {
            const ecoDataByLocationId = await enrichEcoregionLocations(locations);

            await localforage.setItem(LOCATION_ECO_DATA_CACHE_KEY, {
                version: LOCATION_ECO_DATA_CACHE_VERSION,
                ecoDataByLocationId
            });
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <section className="data-onboarding" aria-labelledby="upload-title">
            <div className="data-onboarding__content">
                <h1 className="data-onboarding__headline" id="upload-title">
                    {isProcessing ? 'Processing your eBird data...' : 'Upload your eBird data file'}
                </h1>
                {isProcessing ?
                    <>
                        <div className="data-onboarding__loading-spinner data-onboarding__loading-spinner--centered" aria-hidden="true" />
                        <div className="data-onboarding__copy">
                            <p>{processingMessage} This may take a moment for larger exports.</p>
                        </div>
                    </> :
                    <>
                        <div className="data-onboarding__copy">
                            <p>Click below to select your eBird data ZIP file, or drag and drop the ZIP file here to load and process your eBird data.</p>
                        </div>
                        <div className="data-onboarding__actions">
                            <input type="file"
                                   className="form-control data-onboarding__file-input"
                                   name="dataFileUpload"
                                   id="dataFileUpload"
                                   placeholder="Upload Data File"
                                   disabled={isProcessing}
                                   onChange={onFileChange} />
                        </div>
                        <p className="data-onboarding__question">
                            <button type="button" className="data-onboarding__helper-link data-onboarding__helper-link--button" onClick={onResetDataRequest}>
                                <InfoIcon className="data-onboarding__helper-icon" />
                                Show me that download link again
                            </button>
                        </p>
                    </>
                }
                <div className="data-onboarding__privacy">
                    <InfoIcon className="data-onboarding__privacy-icon" />
                    <span>
                        <strong>Your data is private and secure.</strong>
                        <span>We never share your checklists with anyone.</span>
                    </span>
                </div>
            </div>
            <aside
                className="data-onboarding__hero"
                aria-label="Bird in a natural habitat"
                style={{'--data-onboarding-hero-image': `url("${onboardingHeroImage}")`} as React.CSSProperties}
            >
                <div className="data-onboarding__hero-card">
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">1</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Request your eBird Observations</strong>
                            <span className="data-onboarding__feature-copy">Request your personal eBird data export and check your email for a ZIP file.</span>
                        </span>
                    </div>
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">2</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Sync Your Sightings</strong>
                            <span className="data-onboarding__feature-copy">Drag or select the ZIP file here and BirdMap Explorer will read your eBird history. Your sightings stay on your computer.</span>
                        </span>
                    </div>
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">3</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Explore your world</strong>
                            <span className="data-onboarding__feature-copy">View your birding history as interactive maps, species / family checklists, and customized year views.</span>
                        </span>
                    </div>
                </div>
            </aside>
        </section>
    )
}
